import {
  Add as AddIcon,
  CallSplit as SplitIcon,
  Merge as MergeIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import React, { useRef } from 'react';
import TableStyleControls from './TableStyleControls';
import { IMPORT_ACCEPT } from './tableImport';
import type { useTableEditor } from './useTableEditor';

type Editor = ReturnType<typeof useTableEditor>;

interface TableToolbarProps {
  editor: Editor;
  onImportFile: (file: File) => void;
  busy: boolean;
}

function mergedFocusCell(editor: Editor): { r: number; c: number } | null {
  if (!editor.focus) return null;
  const [fr, fc] = editor.focus;
  for (const row of editor.anchorsByRow) {
    for (const a of row) {
      if (a.r === fr && a.c === fc && (a.colSpan > 1 || a.rowSpan > 1)) return { r: a.r, c: a.c };
    }
  }
  return null;
}

/** Row/column add controls, merge/split, and "Replace from file", shown above the grid on the Desktop tab. */
export default function TableToolbar({ editor, onImportFile, busy }: TableToolbarProps) {
  const rect = editor.selectionRect;
  const canMerge = !!rect && (rect.r2 > rect.r1 || rect.c2 > rect.c1);
  const splitTarget = mergedFocusCell(editor);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <Button size="small" startIcon={<AddIcon />} onClick={() => editor.addRow(editor.rowCount)}>
        Add row
      </Button>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => editor.addColumn(editor.columnCount)}
      >
        Add column
      </Button>
      {canMerge && (
        <Button size="small" startIcon={<MergeIcon />} onClick={editor.mergeSelection}>
          Merge cells
        </Button>
      )}
      {splitTarget && (
        <Button
          size="small"
          startIcon={<SplitIcon />}
          onClick={() => editor.splitAt(splitTarget.r, splitTarget.c)}
        >
          Split cell
        </Button>
      )}
      <Button
        size="small"
        startIcon={<UploadFileIcon />}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        Replace from file
      </Button>
      <TableStyleControls editor={editor} />
      <input
        ref={inputRef}
        type="file"
        accept={IMPORT_ACCEPT}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onImportFile(file);
          event.target.value = '';
        }}
      />
    </Box>
  );
}
